import React, { useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useVideoInterview, useUploadVideo } from '../hooks/videoInterviewHook';
import { useRecaptcha } from '@/modules/shared/hooks/use-recaptcha';
import { ArrowLeft } from 'lucide-react';
import VideoRecorder from './components/VideoRecorder';
import QuestionList from './components/QuestionList';

export default function VideoInterviewPage() {
    const { token } = useParams<{ token: string }>();
    const navigate = useNavigate();
    const { data, isLoading, error } = useVideoInterview(token);
    const uploadVideo = useUploadVideo(token || '');
    const { getToken: getCaptchaToken } = useRecaptcha();

    const handleUpload = useCallback(async (blob: Blob) => {
        try {
            const captchaToken = await getCaptchaToken("interview_upload_video");
            await uploadVideo.mutateAsync({ videoBlob: blob, captchaToken });
            navigate('/video-interview/success');
        } catch (err: unknown) {
            console.error("Upload failed", err);
            const errorMessage = err instanceof Error ? err.message : "Unknown error occurred";
            navigate(`/video-interview/failure?returnUrl=/video-interview/${token}/record&error=${encodeURIComponent(errorMessage)}`);
        }
    }, [uploadVideo, navigate, token, getCaptchaToken]);

    if (!token) return null;

    if (isLoading) {
        return (
            <div className="h-screen w-screen flex items-center justify-center bg-black text-white">
                <p>Loading interview environment...</p>
            </div>
        );
    }

    if (error || !data) {
        return (
            <div className="h-screen w-screen flex items-center justify-center bg-black text-white">
                <p>{error instanceof Error ? error.message : "Error loading interview. Please refresh."}</p>
            </div>
        );
    }

    return (
        <div className="h-screen w-screen flex flex-col bg-muted overflow-hidden">
            {/* Header - Minimal */}
            <div className="h-16 flex items-center px-6 bg-card border-b border-border shrink-0 justify-between shadow-sm z-10">
                <div className="flex items-center gap-4">
                    <button onClick={() => navigate(-1)} className="text-muted-foreground hover:text-foreground transition-colors p-1 hover:bg-muted rounded-full">
                        <ArrowLeft className="w-5 h-5" />
                    </button>
                    <div>
                        <h1 className="text-foreground font-semibold text-lg leading-tight">{data.vacancyTitle}</h1>
                        <p className="text-muted-foreground text-xs">{data.organizationName}</p>
                    </div>
                </div>
                <div className="text-muted-foreground text-sm font-medium bg-muted px-3 py-1 rounded-full">
                    Candidate: {data.candidateName}
                </div>
            </div>

            {/* Main Content - Split Layout */}
            <div className="flex-1 flex overflow-hidden">
                {/* Left Side - Recorder (2/3) */}
                <div className="w-2/3 p-6 flex flex-col justify-center">
                    <div className="flex-1 bg-card rounded-2xl overflow-hidden shadow-xl border border-border ring-4 ring-border">
                        <VideoRecorder onUpload={handleUpload} isUploading={uploadVideo.isPending} />
                    </div>
                </div>

                {/* Right Side - Questions (1/3) */}
                <div className="w-1/3 bg-card border-l border-border shadow-xl z-10 flex flex-col">
                    <QuestionList questions={data.questions} />
                </div>
            </div>
        </div>
    );
}
